import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { RefreshCw, Download, Calendar, CloudRain, Sun, Wind, Thermometer, Navigation } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import DatePickerButton from '../ui/date-picker-button';
import { useToast } from '../../hooks/use-toast';
import apiService from '../../services/api';
import { API_CONFIG } from '../../config/api';
import moment from 'moment';
import { todayISO } from '../../lib/utils';
import SoilingLossChart from '../charts/SoilingLossChart';

const WeatherSensor = () => {
  const [weatherSensors, setWeatherSensors] = useState([]);
  const [selectedSensor, setSelectedSensor] = useState('');
  const [selectedParameters, setSelectedParameters] = useState(['WS_LIVE_POA_POWER_DENSITY']);
  const [chartData, setChartData] = useState([]);
  const [currentData, setCurrentData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  
  // Date states
  const yesterdayStr = moment().subtract(1, 'day').format('YYYY-MM-DD');
  const [startDate, setStartDate] = useState(yesterdayStr);
  const [endDate, setEndDate] = useState(yesterdayStr);
  
  const { toast } = useToast();

  // Weather parameters
  const weatherParameters = [
    { value: 'WS_LIVE_POA_POWER_DENSITY', label: 'Tilt POA Irradiance (kW/m²)', icon: Sun, color: '#ff7300' },
    { value: 'WS_AMBIENT_TEMPERATURE', label: 'Ambient Temperature (°C)', icon: Thermometer, color: '#8884d8' },
    { value: 'WS_MODULE_TEMPERATURE_01', label: 'Module Temperature (°C)', icon: Thermometer, color: '#e74c3c' },
    { value: 'WS_WIND_SPEED', label: 'Wind Speed (m/s)', icon: Wind, color: '#82ca9d' },
    { value: 'WS_WIND_DIRECTION', label: 'Wind Direction (°)', icon: Navigation, color: '#ffc658' },
    { value: 'WS_LIVE_GHI_POWER_DENSITY', label: 'Global Horizontal Irradiance (kW/m²)', icon: Sun, color: '#8dd1e1' }
  ];

  useEffect(() => {
    fetchWeatherSensors();
  }, []);

  useEffect(() => {
    if (selectedSensor) {
      fetchWeatherData();
    }
  }, [selectedSensor, selectedParameters, startDate, endDate]);

  const fetchWeatherSensors = async () => {
    try {
      setIsLoading(true);

      const start = moment(startDate).format('YYYY-MM-DD');
      const end = moment(endDate).format('YYYY-MM-DD');

      const res = await apiService.getForEnergyLoss({
        start_date: `${start} 00:00:00`,
        end_date: `${end} 23:55:00`,
        plant_id: API_CONFIG.PLANT_ID,
        asset_id_list: [API_CONFIG.PLANT_ID]
      });

      const arr = Array.isArray(res.data) ? res.data : (res.data?.body || []);
      let processedSensors = [];

      if (Array.isArray(arr) && arr.length > 0) {
        const eya = arr[0]?.readings?.[0]?.EYA || {};
        const wmsIds = Object.keys(eya.soiling_loss_wms_level || {});
        processedSensors = wmsIds.map(id => ({
          asset_id: id,
          display_name: id,
          type: 'WS',
          make: '-',
          model: '-',
          location: '-',
        }));
      }

      setWeatherSensors(processedSensors);
      if (processedSensors.length > 0) {
        setSelectedSensor(processedSensors[0].asset_id);
      }
    } catch (error) {
      console.error('Error fetching weather sensors:', error);
      toast({
        title: "Error",
        description: "Failed to fetch weather sensor list",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchWeatherData = async () => {
    try {
      setIsLoading(true);
      
      const formattedStartDate = moment(startDate).format('YYYY-MM-DD') + ' 00:00:00';
      const formattedEndDate = moment(endDate).format('YYYY-MM-DD') + ' 23:59:59';
      
      // Fetch OMP data for weather parameters
      const response = await apiService.getOmpDataByTags({
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        plant_id: API_CONFIG.PLANT_ID,
        asset_id_list: [selectedSensor],
        tags_list: selectedParameters
      });

      if (response.data && response.data.json_object) {
        processWeatherData(response.data.json_object);
      }
    } catch (error) {
      console.error('Error fetching weather data:', error);
      toast({
        title: "Error",
        description: "Failed to fetch weather data",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const processWeatherData = (jsonData) => {
    const data = [];
    const timePoints = new Set();
    let latestData = {};
    
    // Extract all unique time points
    selectedParameters.forEach(param => {
      if (jsonData[param]) {
        Object.keys(jsonData[param]).forEach(time => timePoints.add(time));
      }
    });
    
    // Sort time points
    const sortedTimes = Array.from(timePoints).sort();
    
    // Build chart data
    sortedTimes.forEach(time => {
      const dataPoint = { 
        time: moment(time).format('HH:mm'),
        timestamp: time
      };
      
      selectedParameters.forEach(param => {
        if (jsonData[param] && jsonData[param][time]) {
          const value = parseFloat(jsonData[param][time] || 0);
          const paramInfo = weatherParameters.find(p => p.value === param);
          dataPoint[paramInfo?.label || param] = value;
          
          // Store latest value for current data display
          latestData[param] = value;
        }
      });
      
      data.push(dataPoint);
    });
    
    setChartData(data);
    setCurrentData(latestData);
  };

  const handleParameterToggle = (param) => {
    setSelectedParameters(prev => {
      if (prev.includes(param)) {
        // Don't allow deselecting all parameters
        if (prev.length > 1) {
          return prev.filter(p => p !== param);
        }
        return prev;
      } else {
        return [...prev, param];
      }
    });
  };

  const exportData = () => {
    const headers = ['Time', ...selectedParameters.map(param => {
      const info = weatherParameters.find(p => p.value === param);
      return info?.label || param;
    })];
    
    const csvRows = [headers.join(',')];
    
    chartData.forEach(row => {
      const values = [row.time];
      selectedParameters.forEach(param => {
        const info = weatherParameters.find(p => p.value === param);
        values.push(row[info?.label || param] || 0);
      });
      csvRows.push(values.join(','));
    });
    
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `weather-data-${moment().format('YYYY-MM-DD')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    
    toast({
      title: "Success",
      description: "Weather data exported successfully"
    });
  };

  const getWindDirection = (degrees) => {
    const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
    const index = Math.round(degrees / 22.5) % 16;
    return directions[index];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Weather Sensor Monitoring
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Real-time weather conditions and historical data
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline" onClick={exportData}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button onClick={fetchWeatherData} disabled={isLoading || !selectedSensor}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Soiling Loss Chart */}
      <div className="grid grid-cols-1 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Soiling Loss (%)</CardTitle>
          </CardHeader>
          <CardContent>
            <SoilingLossChart startDate={startDate} endDate={endDate} />
          </CardContent>
        </Card>
      </div>

      {/* Weather Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Weather Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Average Temperature</span>
                <span className="font-medium">
                  {currentData.WS_AMBIENT_TEMPERATURE?.toFixed(1) || '-'}°C
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Peak Irradiance</span>
                <span className="font-medium">
                  {currentData.WS_LIVE_POA_POWER_DENSITY?.toFixed(2) || '-'} kW/m²
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Average Wind Speed</span>
                <span className="font-medium">
                  {currentData.WS_WIND_SPEED?.toFixed(1) || '-'} m/s
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Module vs Ambient Temp Diff</span>
                <span className="font-medium">
                  {((currentData.WS_MODULE_TEMPERATURE_01 || 0) - (currentData.WS_AMBIENT_TEMPERATURE || 0)).toFixed(1)}°C
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Weather Impact Analysis</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {currentData.WS_LIVE_POA_POWER_DENSITY > 0.8 && (
                <div className="flex items-start space-x-3">
                  <Sun className="h-5 w-5 text-yellow-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">High Irradiance</p>
                    <p className="text-xs text-gray-500">Optimal conditions for power generation</p>
                  </div>
                </div>
              )}
              {currentData.WS_MODULE_TEMPERATURE_01 > 50 && (
                <div className="flex items-start space-x-3">
                  <Thermometer className="h-5 w-5 text-red-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">High Module Temperature</p>
                    <p className="text-xs text-gray-500">May impact panel efficiency</p>
                  </div>
                </div>
              )}
              {currentData.WS_WIND_SPEED > 10 && (
                <div className="flex items-start space-x-3">
                  <Wind className="h-5 w-5 text-blue-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Strong Wind Conditions</p>
                    <p className="text-xs text-gray-500">Good for module cooling</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WeatherSensor;
